import { ActionSettings, BasicSelect, ICONS, Modal, ToolbarButton } from '@fl/cmsch-fe-library';
import React, { FC, Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Ant } from 'common/ant';
import { ConnectedTable } from 'utils/tables';
import { managementOtherSubjectColumns } from './columns';
import { useManagementOtherSubjectSetup } from '../../hooks/use-management-other-subject-setup';
import { adminAction } from '../../model/action';
import { simpleAdminSelector } from '../../model/selector';
import { ManagementOtherSubjectsTableType } from '../../types/management-other-subjects';
import { ManagementOtherSubjectForm } from '../management-other-subject-form';
const ManagementOtherSubjectTableBase: FC = _ => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(adminAction.getManagementOtherSubjectsOptions(''));
  }, [dispatch]);
  const {
    otherSubjectId,
    otherSubjectsOptions,
    managementOtherSubjectsExtraColumns,
    roleOptions,
    userProfileAndRoles,
    userProfileOptions,
    visibleModal,
    modalTitle,
    otherSubjectDetail,
    isEdit,
    handleCloseModal,
    handleColumnOpenCreateModal,
    handleOpenCreateModalWithoutLimitations,
    handleRoleSearch,
    handleRowOpenCreateModal,
    handleSubmitModal,
    handleUserSearch,
    validate,
    validateDebounced,
    t,
    tCommon,
    searchManagementOtherSubjects,
    onManagementOtherSubjectsTableFetch,
    handleManagementOtherSubjectSelectChangeValue,
    removeConfirmTitle,
    removeRolesForUser,
    removeUsersFromRole,
    rowMapper,
    clearOtherSubjectDetail
  } = useManagementOtherSubjectSetup();
  const leftHeaderCustomElement = useMemo(() => <BasicSelect value={otherSubjectId} options={otherSubjectsOptions} onFieldChange={handleManagementOtherSubjectSelectChangeValue} onSearch={searchManagementOtherSubjects} ignoreSearchMinLength fullWidth testId="other-subject-select" />, [otherSubjectId, otherSubjectsOptions, handleManagementOtherSubjectSelectChangeValue, searchManagementOtherSubjects]);
  const addAccessToUserDisabledTitle = useCallback((_id: number, user: ManagementOtherSubjectsTableType) => t('cannotCreateRecord', {
    user: user.username
  }), [t]);
  const isAddAccessToUserDisabled = useCallback((_id: number, user: ManagementOtherSubjectsTableType) => !user.isSubjectMember, []);
  const actionSettings: ActionSettings<ManagementOtherSubjectsTableType> = useMemo(() => ({
    confirmRowRemove: true,
    extraActions: [{
      id: 'delete',
      role: 'delete',
      callback: removeRolesForUser,
      confirmClick: true,
      confirmTitle: removeConfirmTitle
    }, {
      id: 'create',
      title: t('addAccessToUser'),
      disabledTitle: addAccessToUserDisabledTitle,
      disabled: isAddAccessToUserDisabled,
      role: 'create',
      callback: handleRowOpenCreateModal
    }]
  }), [removeRolesForUser, removeConfirmTitle, addAccessToUserDisabledTitle, isAddAccessToUserDisabled, t, handleRowOpenCreateModal]);
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    title: t('access'),
    role: 'create',
    disabled: otherSubjectId.isEmpty,
    disabledTitle: t('needSelectOtherSubject'),
    onClick: handleOpenCreateModalWithoutLimitations
  }], [otherSubjectId, t, handleOpenCreateModalWithoutLimitations]);
  const computedColumns = useMemo(() => otherSubjectId.isFull ? managementOtherSubjectColumns(managementOtherSubjectsExtraColumns, t, tCommon, removeUsersFromRole, handleColumnOpenCreateModal) : [], [otherSubjectId.isFull, managementOtherSubjectsExtraColumns, t, tCommon, removeUsersFromRole, handleColumnOpenCreateModal]);
  return <Fragment>
            <ConnectedTable tableId="managementOtherSubjects" columns={computedColumns} onFetchTableData={onManagementOtherSubjectsTableFetch} actionSettings={actionSettings} headerButtons={headerButtons} rowDataToTableRowMapper={rowMapper} rowDataPageSelector={simpleAdminSelector.managementOtherSubjectsPage} withoutVerticalScrollbar leftHeaderCustomElement={leftHeaderCustomElement} paginationPosition="none" emptyTableMessages={{
      noColumnsVisible: t('needSelectOtherSubject')
    }} emptyTableBodyContent={ICONS.chooseOption} lazyLoadingLimit={20} showAlsoIfEmpty data-sentry-element="ConnectedTable" data-sentry-source-file="index.tsx" />
            {otherSubjectId.isFull && <Modal title={modalTitle} visible={visibleModal} onCancel={handleCloseModal} footer={null}>
                     <Ant.Row>
                         <Ant.Col xs={24}>
                             <ManagementOtherSubjectForm isEdit={isEdit} dataForEdit={otherSubjectDetail} userProfileOptions={userProfileOptions} roleOptions={roleOptions} userProfileAndRole={userProfileAndRoles} subjectId={otherSubjectId.orCrash('Other subject not found!')} onRoleSearch={handleRoleSearch} onCancel={handleCloseModal} onSubmit={handleSubmitModal} onUserSearch={handleUserSearch} onSyncValidate={validateDebounced} validate={validate} clearFormValues={clearOtherSubjectDetail} />
                         </Ant.Col>
                     </Ant.Row>
                 </Modal>}
        </Fragment>;
};

/** Other-subject management table. */
export const ManagementOtherSubjectTable = memo(ManagementOtherSubjectTableBase);